// Schema groups — clinic-level grouping of per-doctor schemas.
//
// Each schema is one doctor, so a multi-doctor clinic spans several schemas.
// `public.schema_settings."Group"` labels which clinic a schema belongs to.
// Mirrors backend/api/schema_groups.py — keep the two in step.
//
//   ⚠ The column is literally named "Group": capitalised AND a reserved SQL
//     keyword. `.select('schema_name, Group')` would be lowercased by PostgREST
//     to a column that doesn't exist, so reads here use `.select("*")` and pick
//     `row.Group` off the result. Writes are fine — `{ Group: value }` is a
//     plain JSON key, no quoting involved.

/** The dropdown mixes groups and bare schemas; group options carry this prefix. */
export const GROUP_PREFIX = "group:";

export const GROUP_COLUMN = "Group";

/** Case/whitespace-insensitive identity for a group name. "" when ungrouped. */
export function groupKey(value) {
  return String(value ?? "").trim().toLowerCase();
}

/** Group label as STORED: trimmed, or null when blank (one representation, not two). */
export function normalizeGroup(value) {
  const cleaned = String(value ?? "").trim();
  return cleaned || null;
}

export function isGroupChoice(value) {
  return String(value ?? "").startsWith(GROUP_PREFIX);
}

/** `{ kind: "group" | "schema", name }` for a dropdown value. */
export function parseGroupChoice(value) {
  const raw = String(value ?? "").trim();
  if (isGroupChoice(raw)) {
    const name = raw.slice(GROUP_PREFIX.length).trim();
    // A bare "group:" is a typo, not a group — report it as a schema so the
    // caller's own validation rejects it instead of expanding to nothing.
    if (name) return { kind: "group", name };
  }
  return { kind: "schema", name: raw };
}

export function groupChoiceValue(name) {
  return `${GROUP_PREFIX}${name}`;
}

/**
 * schema_settings rows → [{ schema_name, group }], sorted by schema name.
 * Legacy multi-schema rows ('public,Clinic5') are dropped — they aren't real
 * schemas, and the Fax Numbers modal filters them the same way.
 *
 * IDEMPOTENT: accepts both the raw database shape (`Group`) and its own output
 * (`group`). Callers keep the normalised form in React state and then hand it
 * straight back to `groupsFromRows` / `groupForSchema`; reading only `Group`
 * made every one of those second passes see an ungrouped world.
 */
export function rowsToSchemaGroups(rows) {
  return (rows || [])
    .map((r) => ({
      schema_name: String(r?.schema_name ?? "").trim(),
      group: normalizeGroup(r?.[GROUP_COLUMN] ?? r?.group),
    }))
    .filter(
      (r) => r.schema_name && !r.schema_name.includes(",") && !r.schema_name.includes(" ")
    )
    .sort((a, b) => a.schema_name.localeCompare(b.schema_name));
}

/**
 * [{ name, key, schemas[] }] — one entry per distinct group, sorted.
 * Two schemas labelled "Non-Clinical" and "non-clinical" are ONE group; the
 * label that sorts first is shown, matching the backend's `group_names`.
 */
export function groupsFromRows(rows) {
  const byKey = new Map();
  for (const row of rowsToSchemaGroups(rows)) {
    const key = groupKey(row.group);
    if (!key) continue;
    const existing = byKey.get(key);
    if (existing) {
      existing.schemas.push(row.schema_name);
      if (row.group < existing.name) existing.name = row.group;
    } else {
      byKey.set(key, { name: row.group, key, schemas: [row.schema_name] });
    }
  }
  return [...byKey.values()]
    .map((g) => ({ ...g, schemas: g.schemas.sort((a, b) => a.localeCompare(b)) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Schema names carrying no group label — offered on their own in the dropdown. */
export function ungroupedSchemasFromRows(rows) {
  return rowsToSchemaGroups(rows)
    .filter((r) => !groupKey(r.group))
    .map((r) => r.schema_name);
}

/**
 * Options for the patient-approval "Confirm schema" dropdown, as
 * [{ value, label }] sorted by schema name.
 *
 * The ONLY source is schema_settings — the configured list of real schemas,
 * and the same set the Schema groups editor manages. It used to be the
 * matcher's `searched_schemas` unioned with every `schema` string on any loaded
 * signup row, which let things that aren't schemas in: comma-joined values like
 * "public,Clinic5", and stale tenant names carried on old user rows.
 *
 * `draftValue` (the currently selected/pre-filled schema) is appended and
 * MARKED when it isn't configured. Dropping it would be worse than showing it:
 * a <select> whose value matches no option renders BLANK while state still
 * holds that value, so an admin would see an empty box and Approve would submit
 * a schema they never saw. Group choices are ignored here — they aren't schemas.
 */
export function schemaOptionsFromRows(rows, draftValue) {
  const seen = new Set();
  const options = [];
  for (const row of rowsToSchemaGroups(rows)) {
    const key = row.schema_name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    options.push({ value: row.schema_name, label: row.schema_name });
  }
  options.sort((a, b) => a.value.localeCompare(b.value));

  const drafted = String(draftValue ?? "").trim();
  if (drafted && !isGroupChoice(drafted) && !seen.has(drafted.toLowerCase())) {
    options.push({ value: drafted, label: `${drafted} — not in schema_settings` });
  }
  return options;
}

/** Members present in `subset`, in the group's own order. [] stays []. */
function narrowToMembers(members, subset) {
  const wanted = new Set(
    (subset || []).map((s) => String(s ?? "").trim().toLowerCase()).filter(Boolean)
  );
  return (members || []).filter((m) => wanted.has(String(m).toLowerCase()));
}

/**
 * The group members an admin actually ticked, in the group's own order.
 * Mirrors `filter_group_subset` in backend/api/schema_groups.py.
 *
 * Case-insensitive, and anything not in the group is dropped — the backend
 * drops it too, so the button label can never promise a schema the approval
 * won't reach. A missing/empty subset means ALL members: the dropdown opens
 * fully ticked, which is the whole-group behaviour that existed before it.
 */
export function filterGroupSubset(members, subset) {
  const all = (members || []).filter(Boolean);
  if (!subset || !subset.length) return [...all];
  const wanted = new Set(
    subset.map((s) => String(s ?? "").trim().toLowerCase()).filter(Boolean)
  );
  if (!wanted.size) return [...all];
  return all.filter((m) => wanted.has(String(m).toLowerCase()));
}

/**
 * What the patient-approval controls should SHOW and what Approve should
 * SUBMIT, for one draft. Both used to derive this separately, which is how a
 * pre-filled grouped schema could render as a blank <select> while still being
 * submitted.
 *
 * The dropdown lists clinic groups plus the schemas that are in none. A grouped
 * schema is reached THROUGH its clinic — pick the group, then untick the rest —
 * so it is deliberately not also listed on its own. That means a draft holding
 * a bare grouped schema (the matcher pre-fills one) has no matching option, and
 * a <select> whose value matches no option renders EMPTY while state still
 * holds the value Approve would send. So it is presented as its group with just
 * that member ticked, which is the same approval either way.
 *
 * Returns { group, members, picked, selectValue }:
 *   group       the clinic in play, or null for an ungrouped/unknown schema
 *   members     every schema in that clinic
 *   picked      the ticked subset (all of `members` when nothing is narrowed)
 *   selectValue what the <select> should display and what Approve should parse
 */
export function resolveApprovalSelection(rows, draftSchema, draftSubset) {
  const parsed = parseGroupChoice(draftSchema);
  const groupName =
    parsed.kind === "group" ? parsed.name : groupForSchema(rows, parsed.name);
  const group = groupName
    ? groupsFromRows(rows).find((g) => groupKey(g.name) === groupKey(groupName)) || null
    : null;
  const members = group ? group.schemas : [];
  // A bare schema inside a clinic DEFAULTS to "just this one of the clinic" —
  // but only until the admin ticks something. This used to be unconditional,
  // so once the matcher pre-filled a bare schema every later tick was
  // discarded and the dropdown looked frozen on one member. (Switching clinic
  // and back appeared to fix it only because that rewrote the draft into
  // `group:<name>`, taking the other branch.)
  const subset =
    draftSubset != null
      ? draftSubset
      : parsed.kind === "schema" && group
        ? [parsed.name]
        : null;
  return {
    group,
    members,
    // null means "not narrowed" -> the whole clinic. An EMPTY array means the
    // admin unticked everything, which must stay empty: filterGroupSubset
    // reads [] as "no narrowing" for wire-compatibility with the backend, so
    // going through it here re-ticked the whole clinic on the last untick.
    picked: !group
      ? []
      : subset == null
        ? [...members]
        : narrowToMembers(members, subset),
    selectValue: group ? groupChoiceValue(group.name) : String(draftSchema ?? ""),
  };
}

/** The group a schema belongs to, or null. Used for the "clinic" badge. */
export function groupForSchema(rows, schemaName) {
  const key = String(schemaName ?? "").trim().toLowerCase();
  if (!key) return null;
  const match = rowsToSchemaGroups(rows).find(
    (r) => r.schema_name.toLowerCase() === key
  );
  return match ? match.group : null;
}
